import { Check } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { ProfileSection } from '../../../../components/profile/ProfileSection';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import {
  EMPLOYMENT_TYPE_LABELS,
  type PersonnelDetail,
  type PersonnelWorkExperience,
} from '../personnelTypes';

/**
 * Work experience — CS Form 212, section V.
 *
 * Reads down a column like the service record and the education and eligibility histories beside it:
 * what they did, where, for how long, at what grade, and whether it counts as government
 * service. A card per position would make that a scroll.
 *
 * **Most recent first**, which is the opposite of the education panel — and both follow their
 * own section of the form. Education is a ladder read from the bottom; a work history is
 * almost always read for "what are they doing now, and what before that".
 */
export function WorkExperiencePanel({
  record,
  onAdd,
  onEdit,
  onDelete,
  onBulkDelete,
  onRestore,
}: {
  record: PersonnelDetail;
  onAdd: () => void;
  onEdit: (row: PersonnelWorkExperience) => void;
  onDelete: (row: PersonnelWorkExperience) => void;
  onBulkDelete: (rows: PersonnelWorkExperience[]) => void;
  onRestore: (row: PersonnelWorkExperience) => void;
}) {

  const [showDeleted, setShowDeleted] = useState(false);

  const rows = useMemo(
    () => (showDeleted ? record.workExperiences : record.workExperiences.filter((x) => x.isActive)),
    [record.workExperiences, showDeleted],
  );

  const fetchPage = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();

      const matched = term
        ? rows.filter((x) =>
            [x.positionTitle, x.agencyName, x.salaryGrade]
              .filter(Boolean)
              .some((value) => value!.toLowerCase().includes(term)),
          )
        : rows;

      const field = query.sort?.replace(/^-/, '') ?? 'periodFrom';
      const descending = query.sort?.startsWith('-') ?? false;
      const direction = descending ? -1 : 1;

      const sorted = [...matched].sort((a, b) => {
        if (field === 'monthlySalary') {
          /*
            A NUMBER, so it compares as one — string order would put 9,000 above 85,000. Rows
            without a salary sort last whichever way the column is pointed: the form accepts a
            row with no figure, and burying those among the amounts would read as a salary of
            nothing.
          */
          const av = a.monthlySalary ?? null;
          const bv = b.monthlySalary ?? null;
          if (av === null && bv === null) return 0;
          if (av === null) return 1;
          if (bv === null) return -1;
          return (av - bv) * direction;
        }

        if (field === 'isGovernmentService') {
          return (Number(a.isGovernmentService) - Number(b.isGovernmentService)) * direction;
        }

        if (field === 'periodTo') {
          /*
            A row still held has no end date, and it is the MOST recent rather than the least:
            nothing can have ended later than a position not yet left. So it sorts to the top
            descending — the opposite of the missing-salary rule above, because here the null
            is a real answer ("present") rather than an unrecorded one.
          */
          const av = a.periodTo ?? null;
          const bv = b.periodTo ?? null;
          if (av === null && bv === null) return 0;
          if (av === null) return direction;
          if (bv === null) return -direction;
          return av.localeCompare(bv) * direction;
        }

        const pick = (row: PersonnelWorkExperience) =>
          field === 'agencyName'
            ? row.agencyName
            : field === 'periodFrom'
              ? row.periodFrom
              : field === 'salaryGrade'
                ? (row.salaryGrade ?? '')
                : field === 'appointmentStatus'
                  ? // By the LABEL, not the enum number: these values carry no ladder — unlike
                    // EducationLevel, whose order is load-bearing — so the reader sorts what
                    // they can see.
                    EMPLOYMENT_TYPE_LABELS[row.appointmentStatus]
                  : row.positionTitle;

        return String(pick(a)).localeCompare(String(pick(b))) * direction;
      });

      const start = (query.page - 1) * query.pageSize;

      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [rows],
  );

  const state = useDataTable<PersonnelWorkExperience>({
    fetchPage,
    pageSize: 10,

    // Most recent first, matching the order the server sends them in.
    defaultSort: '-periodFrom',
  });

  const columns: DataColumn<PersonnelWorkExperience>[] = [
    {
      key: 'positionTitle',
      header: 'Position title',
      width: 220,
      sortable: true,
      render: (x) => <TextCell value={x.positionTitle} />,
    },
    {
      key: 'agencyName',
      header: 'Department / agency / company',
      width: 240,
      sortable: true,
      render: (x) => <TextCell value={x.agencyName} />,
    },
    {
      // Calendar dates — the day a job started, not an instant.
      key: 'periodFrom',
      header: 'From',
      width: 130,
      sortable: true,
      render: (x) => <TextCell value={formatDateOnly(x.periodFrom)} mono />,
    },
    {
      key: 'periodTo',
      header: 'To',
      width: 130,
      sortable: true,
      /*
        "Present", not an em dash. An absent value is a dash everywhere in this product, and
        this is not one: a row with no end date is the form's own PRESENT — a real answer,
        saying the position is still held. A dash would read as "we did not record when it
        ended", which is a different fact.
      */
      render: (x) => <TextCell value={x.periodTo ? formatDateOnly(x.periodTo) : 'Present'} mono />,
    },
    {
      key: 'salaryGrade',
      header: 'SG',
      width: 90,
      sortable: true,
      /*
        `mono` but NOT `numeric`: it reads as a code, and it can be one — "Band B" is a real
        answer here, because this column is text rather than a link into the salary schedule.
        `numeric` would also dim a grade of 0, which is a grade rather than a quantity of none.
      */
      render: (x) => <TextCell value={x.salaryGrade} mono />,
    },
    {
      key: 'stepIncrement',
      header: 'Step',
      width: 90,
      // Not sortable: the server does not order by it and neither does this panel — a header
      // that silently fell back to the default sort reads as a broken control.
      render: (x) => <TextCell value={x.stepIncrement} mono />,
    },
    {
      key: 'monthlySalary',
      header: 'Monthly salary',
      width: 150,
      align: 'right',
      sortable: true,
      // Money: two decimals always, no symbol, an em dash for an absent amount — all of which
      // `formatMoney` owns. `numeric` is what mutes a zero, which is a real salary.
      render: (x) => <TextCell value={formatMoney(x.monthlySalary)} mono numeric />,
    },
    {
      key: 'appointmentStatus',
      header: 'Status of appointment',
      width: 170,
      sortable: true,
      /*
        Plain text, not a <StatusText> pill. A pill marks a RECORD's own state — active,
        deleted, locked — and this is a fact about a job somebody held years ago. Rendering it
        as a pill would put a second status vocabulary in a column beside the row's real one.
      */
      render: (x) => <TextCell value={EMPLOYMENT_TYPE_LABELS[x.appointmentStatus]} />,
    },
    {
      key: 'isGovernmentService',
      header: "Gov't service",
      width: 130,
      sortable: true,
      /*
        The form's own words, and its own answers: "Indicate 'yes' under government service if
        the position held is in the public or government employment or 'no' if held in the
        private employment." Yes/No rather than a tick, because a blank cell beside a ticked
        one cannot be told from a row nobody answered — and this column is what length of
        government service is counted from.
      */
      render: (x) => <TextCell value={x.isGovernmentService ? 'Yes' : 'No'} />,
    },
    {
      /*
        A panel whose rows can be deleted owes a status column: without one a deleted row
        carries no marker while Show deleted is on, which is the one state a row must
        never be silent about. This panel offered Show deleted and had no column to show
        it in. `<DataTablePanel>` moves it to the end of the data columns itself.
      */
      key: 'status',
      header: 'Status',
      width: 110,
      status: true,
      render: (x) =>
        x.isActive ? (
          <StatusText tone="active">Active</StatusText>
        ) : (
          <StatusText tone="inactive">Deleted</StatusText>
        ),
    },
  ];

  return (
    <ProfileSection>
      <DataTablePanel<PersonnelWorkExperience>
        id="personnel-work-experience"
        state={state}
        /*
          The first column opens the row as a key/value preview. A table row compares records;
          it is a poor way to read ONE, because the columns are spread across a scroll and the
          narrow ones truncate. The preview is built from these same `columns`, so it cannot
          drift from the table or miss one somebody adds.
        */
        // The size of the modal that EDITS this record, so reading a row and correcting it
        // happen at one width rather than the dialog changing shape between them.
        rowPreview={{ title: 'Work experience', size: '2xl' }}
        columns={columns}
        rowKey={(x) => x.id}
        searchPlaceholder="Search position, employer or grade"
        onCreate={onAdd}
        createPermission="personnel.update"
        createDisabledReason={
          record.isActive
            ? null
            : 'This employee is deleted. Restore them before editing the record.'
        }
        /*
          The kebab's entries. The shell owns the gutter, the menu and the right-click
          shortcut — this is only what goes IN the menu, and the same array feeds the table
          and the list, so the two cannot offer different actions on one row.

          Delete and Restore are the two halves of one state, so only the applicable one is
          offered. That is not the disable-for-state case: "restore a live row" is not
          something the reader is part-way through, it is simply not an action here.
        */
        rowActions={(x) => [
          {
            key: `edit-${x.id}`,
            label: 'Edit',
            permission: 'personnel.update',
            disabledReason: x.isActive
              ? null
              : 'This record is deleted. Restore it before editing.',
            onClick: () => onEdit(x),
          },
          x.isActive
            ? {
                key: `delete-${x.id}`,
                label: 'Delete',
                permission: 'personnel.update',
                destructive: true,
                onClick: () => onDelete(x),
              }
            : {
                key: `restore-${x.id}`,
                label: 'Restore',
                permission: 'personnel.update',
                onClick: () => onRestore(x),
              },
        ]}
        otherInfo={(x) => ({
          createdAt: x.createdAt,
          createdByName: x.createdByName,
          updatedAt: x.updatedAt,
          updatedByName: x.updatedByName,
        })}
        /*
          The checkbox gutter is ON, which is the default. It carried `selectable={false}`,
          and that reading is the one CLAUDE.md names: the opt-out is only for a table nothing
          can ever be done to, never for one whose bulk action is merely unbuilt. Every row
          here can be deleted one at a time, so the column belongs — and the bulk action ships
          with it rather than leaving a selection with nowhere to go.
        */
        bulkActions={(selected) => {
          const deletable = selected.filter((x) => x.isActive);

          return [
            {
              key: 'delete-work-experiences',
              label: `Delete ${deletable.length} work experience record${deletable.length === 1 ? '' : 's'}`,
              permission: 'personnel.update',
              destructive: true,
              disabledReason: !record.isActive
                ? 'This employee is deleted. Restore them before editing the record.'
                : deletable.length === 0
                  ? 'Every selected work experience record is already deleted.'
                  : null,
              onClick: () => onBulkDelete(deletable),
            },
          ];
        }}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            icon: showDeleted ? <Check className="h-4 w-4" aria-hidden="true" /> : undefined,
            onClick: () => setShowDeleted((current) => !current),
          },
        ]}
        emptyMessage="No work experience recorded yet."
        emptySearchMessage="No work experience matches that search."
      />
    </ProfileSection>
  );
}
